import { Show } from "solid-js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@jornada/shared/solid";
import {
  baselineCatalog,
  classifyCategoryTotals,
  cn,
  type EmployeeProfileRecord,
  getEmployeeProfilesQuery,
  getPendingProposedCountQuery,
  getTeamUsageByCategoryQuery,
  productivityRatio,
  queryKeys,
} from "@jornada/shared";
import { msg } from "@lingui/core/macro";
import { BarChart3 } from "@fleet/icons";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";

interface StatsSectionProps {
  userId: string;
}

type UsageRow = { category: string | null; total_secs: number };

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const profiles = createPowerSyncQuery<EmployeeProfileRecord>({
    queryKey: () => queryKeys.workforce.profiles(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getEmployeeProfilesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const pending = createPowerSyncQuery<{ total: number }>({
    queryKey: () => queryKeys.workforce.pendingCount(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getPendingProposedCountQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const usage = createPowerSyncQuery<UsageRow>({
    queryKey: () => queryKeys.workforce.usageByCategory("__team__"),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTeamUsageByCategoryQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const employeeCount = () => (profiles.data ?? []).length;
  const pendingCount = () => pending.data?.[0]?.total ?? 0;
  const teamRatioPct = () =>
    Math.round(
      productivityRatio(classifyCategoryTotals(usage.data ?? [], baselineCatalog())) * 100,
    );

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={t(msg`Employees`)}
            value={employeeCount()}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={profiles.isPending}
          />
          <StatCard
            label={t(msg`Pending review`)}
            value={pendingCount()}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={pending.isPending}
          />
          <StatCard
            label={t(msg`Team productivity`)}
            value={teamRatioPct()}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={usage.isPending}
          />
        </div>

        <Show when={!usage.isPending}>
          <div class="space-y-2">
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t(msg`Team productivity`)}</span>
              <span class="font-bold text-foreground">{teamRatioPct()}%</span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full bg-green-500 transition-transform duration-500 origin-left"
                style={{ transform: `scaleX(${teamRatioPct() / 100})` }}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
